import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { CollapsibleSection } from "./CollapsibleSection";
import type { Citation } from "./types";

function isSafeHref(href: string | null | undefined): href is string {
  if (!href) return false;
  if (href.startsWith("/")) return true;
  return /^https?:\/\//i.test(href);
}

const Wrapper = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
  padding-top: 10px;
  border-top: ${({ theme }) => theme.resolve("border_md")} dashed
    ${({ theme }) => theme.secondaryBorder};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const chipStyles = `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  border-radius: 12px;
`;

const Chip = styled.a`
  ${chipStyles}
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_brand_mixed")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.brandColor};
  color: ${({ theme }) => theme.brandColor};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const PlainChip = styled.span`
  ${chipStyles}
  background-color: #f1f1f1;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  color: ${({ theme }) => theme.primaryTextColor};
`;

const NumberLabel = styled.span`
  opacity: 0.7;
  font-variant-numeric: tabular-nums;
`;

export function Citations({ citations }: { citations: Citation[] }) {
  const { t } = useTranslation();
  if (!citations || citations.length === 0) return null;
  return (
    <Wrapper>
      <CollapsibleSection
        label={t("References")}
        collapseLabel={t("Collapse")}
        expandLabel={t("Expand")}
      >
        <ChipRow>
          {citations.map((c, i) => {
            const label = c.name || c.product_number || c.id;
            const key = `${c.id}-${i}`;
            if (isSafeHref(c.url)) {
              const isExternal = /^https?:\/\//i.test(c.url);
              const targetProps = isExternal
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {};
              return (
                <Chip key={key} href={c.url} {...targetProps}>
                  {label}
                  {c.product_number && (
                    <NumberLabel>· {c.product_number}</NumberLabel>
                  )}
                </Chip>
              );
            }
            return (
              <PlainChip key={key}>
                {label}
                {c.product_number && (
                  <NumberLabel>· {c.product_number}</NumberLabel>
                )}
              </PlainChip>
            );
          })}
        </ChipRow>
      </CollapsibleSection>
    </Wrapper>
  );
}
